// chainDistances.js - 2026-10-25 (Dr. Tatar: Tab 1 sets the troops, the troops set the facilities, the facilities stand at the parent HQ
// position plus an offset, and the chain distances follow from those positions; the planner may override any leg).
//
// One owner per leg of the medical chain. Order of precedence for each leg:
//   1. OVERRIDE  - medical_configuration.distance_overrides[leg], typed by the planner (Tab 3) or set by a Tab 6 COA modifier
//   2. FACILITIES - the gap between the placed facilities (position_feba_km), Role 1 -> Role 2B -> Role 2E -> Role 3
//   3. LEGACY    - the older typed values (failsafe only, for a scenario that has no placed facilities)
//   4. GENERIC   - a generic planning distance for a level the planner says exists but no tab placed (a brigade planner knows the
//                  upper facilities exist, not always where; a corps planner may use a generic distance to Role 4). Shown as such.
// A level the planner marks as not available (Tab 3 switches r2e_accessible, r3_accessible) has no leg: NOT_AVAILABLE.
// The engine reads the result from medical_configuration.chain_distances (added by applyChainDistances at the start of a run).

export const CHAIN_LEGS = ['r1_to_r2b', 'r2b_to_r2e', 'r2e_to_r3'];

export const CHAIN_LEG_LABELS = {
  r1_to_r2b: 'Role 1 to Role 2 Basic',
  r2b_to_r2e: 'Role 2 Basic to Role 2 Enhanced',
  r2e_to_r3: 'Role 2 Enhanced to Role 3'
};

const LEG_ENDS = {
  r1_to_r2b: ['ROLE1', 'ROLE2_BASIC'],
  r2b_to_r2e: ['ROLE2_BASIC', 'ROLE2_ENHANCED'],
  r2e_to_r3: ['ROLE2_ENHANCED', 'ROLE3']
};

const TYPE_ALIASES = { ROLE2B: 'ROLE2_BASIC', ROLE2E: 'ROLE2_ENHANCED', ROLE_2_BASIC: 'ROLE2_BASIC', ROLE_2_ENHANCED: 'ROLE2_ENHANCED' };
export const GENERIC_LEG_KM = { r2b_to_r2e: 40, r2e_to_r3: 70 };   // EDUCATED GUESS (the older Tab 3 defaults)
const MIN_LEG_KM = 1;   // two facilities never stand on the same spot

const typeOf = (f) => {
  const t = String(f?.facility_type || f?.type || '').toUpperCase();
  return TYPE_ALIASES[t] || t;
};

const meanPosition = (facilities, type) => {
  const pos = (facilities || []).filter((f) => typeOf(f) === type && Number.isFinite(Number(f.position_feba_km))).map((f) => Number(f.position_feba_km));
  return pos.length ? pos.reduce((a, b) => a + b, 0) / pos.length : null;
};

const round1 = (x) => Math.round(x * 10) / 10;
const positive = (x) => (Number.isFinite(Number(x)) && Number(x) > 0 ? Number(x) : null);

/** Distance of each leg from the placed facilities (km, null where an end facility is not placed). Several facilities of a type: their mean position. */
export function deriveChainFromFacilities(facilities) {
  const out = {};
  CHAIN_LEGS.forEach((leg) => {
    const [a, b] = LEG_ENDS[leg].map((t) => meanPosition(facilities, t));
    out[leg] = a === null || b === null ? null : round1(Math.max(MIN_LEG_KM, Math.abs(b - a)));
  });
  return out;
}

/** The older typed values; used only when a leg has neither an override nor two placed facilities. */
export function legacyChain(mc) {
  const route = (k) => mc?.evacuation_routes?.[k]?.primary?.distance_km;
  return {
    r1_to_r2b: positive(mc?.mtf_distances?.role2_distance_km),
    r2b_to_r2e: positive(mc?.r2e_distance_km),
    r2e_to_r3: positive(route('r2e_to_r3'))
  };
}

/** { leg: { km, source, derived_km } } with source OVERRIDE | FACILITIES | LEGACY | GENERIC | NOT_AVAILABLE | MISSING. derived_km is what the facilities give (or null). */
export function resolveChainDistances(mc, forceFacilities) {
  const placed = Array.isArray(mc?.facilities) && mc.facilities.length ? mc.facilities : forceFacilities;   // Tab 3 saved list first, else the facilities Tab 1 built
  const derived = deriveChainFromFacilities(placed);
  const legacy = legacyChain(mc);
  const overrides = mc?.distance_overrides || {};
  const legs = {};
  const unavailable = { r2b_to_r2e: mc?.r2e_accessible === false, r2e_to_r3: mc?.r3_accessible === false };
  CHAIN_LEGS.forEach((leg) => {
    const o = positive(overrides[leg]);
    if (unavailable[leg]) legs[leg] = { km: null, source: 'NOT_AVAILABLE', derived_km: derived[leg] };
    else if (o !== null) legs[leg] = { km: o, source: 'OVERRIDE', derived_km: derived[leg] };
    else if (derived[leg] !== null) legs[leg] = { km: derived[leg], source: 'FACILITIES', derived_km: derived[leg] };
    else if (legacy[leg] !== null) legs[leg] = { km: legacy[leg], source: 'LEGACY', derived_km: null };
    else if (GENERIC_LEG_KM[leg]) legs[leg] = { km: GENERIC_LEG_KM[leg], source: 'GENERIC', derived_km: null };
    else legs[leg] = { km: null, source: 'MISSING', derived_km: null };
  });
  return legs;
}

/** Km of one leg, or null. */
export function chainLegKm(mc, leg) {
  return resolveChainDistances(mc)[leg].km;
}

/** Sets a planner override on a copy-safe config (mutates mc.distance_overrides; used by Tab 3 and the Tab 6 COA modifiers). */
export function setLegOverride(mc, leg, km) {
  if (!mc || !CHAIN_LEGS.includes(leg)) return mc;
  mc.distance_overrides = { ...(mc.distance_overrides || {}) };
  if (positive(km) === null) delete mc.distance_overrides[leg];
  else mc.distance_overrides[leg] = Number(km);
  return mc;
}

/**
 * A copy of the medical configuration with chain_distances added and the keys the engine reads set from it:
 * mtf_distances.role2_distance_km (Role 1 -> Role 2B), mtf_distances.role2_basic_km (same leg, older engine key),
 * r2e_distance_km (Role 2B -> Role 2E). The capacity model reads chain_distances.r1_to_r2b directly.
 */
export function applyChainDistances(mc, forceFacilities) {
  if (!mc || typeof mc !== 'object') return mc;
  const legs = resolveChainDistances(mc, forceFacilities);
  const next = { ...mc, chain_distances: legs };
  if (legs.r1_to_r2b.km !== null) {
    next.mtf_distances = { ...(mc.mtf_distances || {}), role2_distance_km: legs.r1_to_r2b.km, role2_basic_km: legs.r1_to_r2b.km };
  }
  if (legs.r2b_to_r2e.km !== null) next.r2e_distance_km = legs.r2b_to_r2e.km;
  return next;
}
